import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const requiredCoverages = [
  {
    icon: 'ri-shield-check-line',
    title: 'General Liability (GL)',
    desc: 'Covers third-party bodily injury and property damage. For mining operations, GL limits are typically much higher than standard businesses due to the severity of potential claims and the number of people on site.',
    tag: 'Required',
  },
  {
    icon: 'ri-first-aid-kit-line',
    title: 'Workers Compensation',
    desc: 'Required for all employees, including high-risk mining classifications. Coal mining carries some of the highest workers comp rates in any industry — accurate classification and payroll reporting are critical.',
    tag: 'Required by Law',
  },
  {
    icon: 'ri-truck-line',
    title: 'Commercial Auto',
    desc: 'Covers hauling trucks, service vehicles, and fleet operations. Coal hauling trucks and heavy transport vehicles carry significant liability exposure — every vehicle must be properly listed and rated.',
    tag: 'Required for Vehicles',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipment / Inland Marine',
    desc: 'Covers heavy machinery and mobile equipment — continuous miners, loaders, conveyors, and more. Equipment must be accurately scheduled at current replacement value (TIV) to avoid being underinsured at claim time.',
    tag: 'Essential',
  },
  {
    icon: 'ri-umbrella-line',
    title: 'Umbrella Insurance',
    desc: 'Provides additional liability protection above your base GL and auto policies. Given the severity of mining claims, umbrella coverage is not optional — it\'s a core part of any properly structured mining program.',
    tag: 'Strongly Recommended',
  },
];

const additionalRequirements = [
  {
    icon: 'ri-leaf-line',
    title: 'Pollution Liability',
    desc: 'Covers environmental damage and cleanup costs from mining operations. Standard GL policies exclude pollution claims — coal operations with slurry ponds, runoff risk, or chemical exposure need this coverage separately.',
  },
  {
    icon: 'ri-lungs-line',
    title: 'Black Lung / Occupational Disease',
    desc: 'Black lung (coal workers\' pneumoconiosis) is a long-tail occupational disease exposure. Operators need to understand how their policies address occupational disease claims and whether separate coverage is needed.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'MSHA Compliance Documentation',
    desc: 'The Mine Safety and Health Administration (MSHA) requires specific safety documentation and compliance records. Carriers writing mining risks will often require proof of MSHA compliance before binding coverage.',
  },
  {
    icon: 'ri-contract-line',
    title: 'Proof of Insurance for Contracts',
    desc: 'Most mining contracts and job site agreements require certificates of insurance with specific limits and additional insured endorsements. Make sure your policy is structured to meet contract requirements before work begins.',
  },
];

const keyConsiderations = [
  {
    title: 'High Limits Are Often Required',
    desc: 'Due to the severity of mining claims — equipment losses, injuries, and environmental events — coverage limits must be significantly higher than standard commercial policies. Minimum GL limits of $2M–$5M are common, with umbrella on top.',
  },
  {
    title: 'Equipment Values Must Be Accurately Scheduled',
    desc: 'Total Insured Value (TIV) matters enormously in mining. A single continuous miner or longwall system can be worth millions. If your equipment schedule is outdated or inaccurate, you may receive far less than replacement cost at claim time.',
  },
  {
    title: 'New Ventures Have Limited Market Options',
    desc: 'New mining operations without loss history often face limited carrier options. Many standard markets will not write new ventures — working with an agency that has access to E&S (Excess & Surplus) markets is critical for new operators.',
  },
  {
    title: 'Many Standard Carriers Will Not Write Mining Risks',
    desc: 'Coal mining is classified as a high-risk industry. Most standard insurance carriers decline to write mining risks entirely. You need an agency with access to specialty markets that understand and actively write mining operations.',
  },
];

export default function CoalMineInsuranceArticle() {
  useEffect(() => {
    document.title = 'Coal Mine Insurance Requirements Appalachia KY TN VA | Summit Appalachia';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'A breakdown of the specialized insurance coverage required for coal mining operations in Kentucky, Tennessee, and Virginia — including GL, workers comp, equipment, and pollution liability.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/resources/coal-mine-insurance`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources/coal-mine-insurance#article`,
      url: `${import.meta.env.VITE_SITE_URL}/resources/coal-mine-insurance`,
      headline: 'Coal Mine Insurance: What\'s Required for Appalachian Mining Operations',
      description: 'A breakdown of the specialized insurance coverage required for coal mining operations in Kentucky, Tennessee, and Virginia — including GL, workers comp, equipment, and pollution liability.',
      datePublished: '2025-11-01',
      dateModified: '2026-04-06',
      author: { '@type': 'Organization', name: 'Summit Appalachia Insurance' },
      publisher: {
        '@type': 'Organization',
        name: 'Summit Appalachia Insurance',
        url: import.meta.env.VITE_SITE_URL,
      },
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Coal Mining Insurance' },
        { '@type': 'Thing', name: 'High-Risk Business Insurance' },
        { '@type': 'Thing', name: 'Appalachian Industries' },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-coal-mine';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-coal-mine')?.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', import.meta.env.VITE_SITE_URL ?? '/');
    };
  }, []);

  return (
    <div className="font-sans bg-[#000000]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/40cb3de70afec82afd19fc61aa61ac82.jpg"
            alt="Coal Mine Insurance"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            {' '}/ <Link to="/resources" className="hover:text-[#D4AF37] transition-colors">Resources</Link>
            {' '}/ Article
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Articles</span>
            <span className="text-white/30 text-xs font-sans">7 min read · Nov 2025</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Coal Mine Insurance:<br />
            <span className="text-[#D4AF37]">What's Required?</span>
          </h1>
          <p className="text-white/50 text-base font-sans mt-4 max-w-2xl leading-relaxed">
            A quick breakdown of the specialized coverage required for coal mining operations in Appalachia — and why standard policies won't cut it.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 mb-12">
            <p className="text-white/70 text-base font-sans leading-relaxed">
              Coal mining is one of the highest-risk industries in Appalachia. Because of this, <strong className="text-white">standard business insurance is not enough</strong> — operators need specialized coverage to meet legal requirements and protect against major losses. One uncovered claim in this industry can be catastrophic.
            </p>
          </div>
        </div>
      </section>

      {/* Required Coverages */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Required Coverages</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Most coal mining operations will need all five of these coverages in place before operations begin or contracts are signed.
          </p>
          <div className="space-y-5">
            {requiredCoverages.map((cov, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                    <i className={`${cov.icon} text-2xl`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-serif text-xl text-white">{cov.title}</h3>
                      <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest font-sans border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm">{cov.tag}</span>
                    </div>
                    <p className="text-white/55 text-sm font-sans leading-relaxed">{cov.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Additional Requirements */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Common Additional Requirements</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Depending on the operation, you may also need these coverages. Many are required by contract or regulation — not just best practice.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {additionalRequirements.map((req, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37]">
                    <i className={`${req.icon} text-xl`} />
                  </div>
                  <h3 className="font-sans text-sm font-bold text-white">{req.title}</h3>
                </div>
                <p className="text-white/45 text-sm font-sans leading-relaxed">{req.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Considerations */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">Key Considerations</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            These are the factors that most often cause problems for mining operators at claim time — or when trying to get coverage in the first place.
          </p>
          <div className="space-y-4">
            {keyConsiderations.map((item, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-7 h-7 flex items-center justify-center text-[#D4AF37] flex-shrink-0 mt-0.5">
                    <span className="font-serif text-lg font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-sans text-sm font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/50 text-sm font-sans leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Note */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-md p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-alert-line text-xl" />
              </div>
              <h3 className="font-sans text-sm font-bold text-white uppercase tracking-widest">Final Note</h3>
            </div>
            <p className="text-white/60 text-base font-sans leading-relaxed">
              Coal mining insurance is highly specialized. Working with an agency that understands mining risks and has access to the right markets — including E&S carriers — is critical. Don't trust a standard commercial policy to cover a non-standard risk.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0a0a0a] border-t border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Need a Coal Mining Insurance Quote?</h2>
          <p className="text-white/40 text-sm font-sans leading-relaxed mb-8 max-w-xl mx-auto">
            Summit Appalachia Insurance specializes in coal mining risks across KY, TN, and VA. We have access to both standard and E&S markets — and we understand the risks that come with operating in Appalachia.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Custom Quote
            </Link>
            <Link
              to="/resources"
              className="px-8 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
